import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/balance';
import { Player } from '../objects/Player';
import { Enemy } from '../objects/Enemy';
import { AIBot } from '../objects/AIBot';
import { CircularWorld } from '../objects/CircularWorld';
import { TerritoryManager } from '../objects/TerritoryManager';
import { SocketService, ItemCollectPayload } from '../services/socket';
import { useGameStore } from '../store/gameStore';
import { supabase } from '../services/supabase';
import { AudioManager } from '../services/AudioManager';
import { ParticleManager } from '../objects/ParticleManager';
import { ItemManager } from '../objects/ItemManager';
import { MissileManager } from '../objects/MissileManager';
import { resolvePickup, rollMysteryTypes, ITEM_TOAST } from '../config/items';

const DEFAULT_ROOM_ID = '00000000-0000-0000-0000-000000000000';

type PlayerDeathFunctionData = {
  success?: boolean;
  error?: unknown;
};

// 강렬한 색상 팔레트 (높은 채도, 25개 - 20명 사용자 + 5개 AI 봇)
const COLOR_PALETTE = [
  0x0066FF,  // 진한 파랑
  0xFF0044,  // 강렬한 빨강
  0x00CC44,  // 선명한 초록
  0xFFCC00,  // 진한 노랑
  0xAA00FF,  // 강렬한 보라
  0x00CCAA,  // 진한 청록
  0xFF6600,  // 진한 주황
  0xFF0099,  // 진한 핑크
  0x3300FF,  // 진한 인디고
  0x88CC00,  // 선명한 라임
  0x00AAFF,  // 하늘색
  0xFF3366,  // 코랄
  0x33CC99,  // 민트
  0xFFAA00,  // 금색
  0x9933FF,  // 연보라
  0x00FF99,  // 네온 그린
  0xFF5500,  // 다크 오렌지
  0xCC0066,  // 마젠타
  0x6666FF,  // 퍼플 블루
  0xCCCC00,  // 올리브
  0x00DDDD,  // 시안
  0xDD4400,  // 테라코타
  0x7700CC,  // 딥 퍼플
  0x44BB44,  // 에메랄드
  0xEE6688,  // 로즈
];

// AI 봇 닉네임 풀 (한글 전용)
const BOT_NAMES_KR = [
  '램지', '캐롤라인', '캥거루', '트황상', '시진핑',
  '카리나', '닝닝', '고윤정',
  '숏충이', '래버러지', '가즈아', 'NVDA', 'QQQ', 'SOXL', 'ASML'
];

// 고정된 봇 배치 데이터 (위치/색상만 결정론적 - 이름은 랜덤)
// 봇 정체성(이름/색)은 호스트가 bot:move로 브로드캐스트하므로
// 이름이 랜덤이어도 모든 클라이언트에 동일하게 보인다.
const FIXED_BOT_DATA = [
  { colorIndex: 5, angleOffset: 0 },
  { colorIndex: 6, angleOffset: 72 },
  { colorIndex: 7, angleOffset: 144 },
  { colorIndex: 8, angleOffset: 216 },
  { colorIndex: 9, angleOffset: 288 },
];

// 사냥꾼 봇이 플레이어 꼬리를 감지하는 범위 (px)
const HUNTER_DETECTION_RADIUS = 650;
// 공격형 봇 최대 수 (접속자가 늘수록 일반 봇을 줄이고 공격형 위주로 배치)
const MAX_AGGRESSIVE_BOTS = 3;


export class GameScene extends Phaser.Scene {
  private localPlayer?: Player;
  private circularWorld?: CircularWorld;
  private territoryManager?: TerritoryManager;
  private remotePlayers: Map<string, Enemy> = new Map();
  private remoteBots: Map<string, Enemy> = new Map(); // 비호스트용 원격 봇
  private aiBots: AIBot[] = [];
  private socketService?: SocketService;
  private itemManager?: ItemManager;
  private missileManager?: MissileManager;
  private broadcastTimer: number = 0;
  private hudUpdateTimer: number = 0; // 미니맵/영역비율 갱신 스로틀
  private moveBroadcastTick: number = 0; // 플레이어 trail 포함 주기 계산용
  private botBroadcastTimer: number = 0; // 봇 브로드캐스트 타이머
  private audioManager?: AudioManager;
  private particleManager?: ParticleManager;
  private nickname: string = 'Player';
  private roomId: string = DEFAULT_ROOM_ID;
  private usedColorIndices: Set<number> = new Set();
  private usedColors: Set<number> = new Set(); // 실제 사용 중인 색상값 추적
  private isGameOver: boolean = false; // 중복 사망 처리 방지
  private localDeathCompleted: boolean = false;
  private localDeathBroadcasted: boolean = false;
  private localDeathBroadcastPromise?: Promise<void>;
  private roomLeaveStarted: boolean = false;
  private unsubscribeStore?: () => void;
  private remoteCleanupTimer: number = 0;

  // 원형 맵 설정 (2배 확장)
  private worldRadius: number = 3000;
  private worldCenterX: number = 3200;
  private worldCenterY: number = 3200;

  constructor() {
    super({ key: 'GameScene' });
  }

  // 멀티플레이어 데이터
  private playerId: string = '';
  private playSessionId: string = '';
  private spawnPosition?: { x: number; y: number };
  private playerColor?: string;
  private isHost: boolean = false;
  private offlineMode: boolean = false;

  // ===== Sticky 호스트(봇 시뮬레이션 담당) 상태 =====
  // 호스트는 한번 정해지면 유지되고, 다음 경우에만 바뀐다:
  //  - 호스트가 방을 떠나거나 봇 브로드캐스트가 끊겼을 때 (가장 작은 ID가 인계)
  //  - 호스트 탭이 백그라운드로 가서 시뮬레이션이 멈출 때 (스스로 사임)
  //  - 호스트가 중복 감지됐을 때 (bot:move의 host_id 비교, 작은 ID가 이김)
  private botBroadcastTick: number = 0; // trail 포함 주기 계산용
  private knownHostId?: string; // 마지막 bot:move를 보낸 호스트
  private lastBotFeedAt: number = 0; // 마지막 bot:move 수신 시각
  private lastPresentIds: string[] = []; // 마지막 Presence sync 기준 접속자 (정렬됨, 자신 포함)
  private sceneStartAt: number = 0;
  private visibilityHandler?: () => void;
  private readonly BOT_FEED_STALE_MS = 3000; // 봇 피드가 이 시간 동안 없으면 호스트 부재로 판단
  private readonly NO_PRESENCE_FALLBACK_MS = 8000; // Presence 자체가 안 오면 단독 호스트로 폴백

  // 영역 스냅샷 주기 전송 (이벤트 유실/호스트 교체 등으로 어긋난 영역을 자가 치유)
  private territorySnapshotTimer: number = 0;
  private readonly TERRITORY_SNAPSHOT_INTERVAL_MS = 12000;

  // 봇 킬 중복 방지 (비호스트의 킬 요청 쿨다운 / 호스트의 사망 처리 디바운스)
  private botKillCooldown: Map<string, number> = new Map();
  private lastBotDeathAt: Map<string, number> = new Map();
  // 로컬 예측으로 죽인 봇의 "죽기 전 정체성" 잔여 패킷 억제용
  // (리스폰 패킷이 오면 새 정체성이므로 자동 해제)
  private botKillSuppress: Map<string, { until: number; color: number; nickname: string }> = new Map();

  init(data: {
    nickname?: string;
    roomId?: string;
    playerId?: string;
    spawnPosition?: { x: number; y: number };
    color?: string;
    players?: { id: string; nickname: string; color: string }[];
    offline?: boolean;
  }) {
    this.nickname = data.nickname || `Player_${Math.floor(Math.random() * 1000)}`;
    this.roomId = data.roomId || DEFAULT_ROOM_ID;
    this.playerId = data.playerId || crypto.randomUUID();
    this.playSessionId = crypto.randomUUID();
    this.spawnPosition = data.spawnPosition;
    this.playerColor = data.color;
    this.offlineMode = data.offline === true;

    // 호스트는 Presence 동기화 시점에 "현재 접속 중인 클라이언트" 기준으로 선출한다.
    // (입장 시점의 DB 스냅샷으로 1회만 정하면 늦게 입장한 더 작은 ID가
    //  자신을 호스트로 판단해 호스트가 중복되는 문제가 있었음)
    // 오프라인 테스트 모드만 즉시 호스트로 시작한다.
    this.isHost = this.offlineMode;

    console.log('GameScene init:', {
      roomId: this.roomId,
      playerId: this.playerId,
      nickname: this.nickname,
      offlineMode: this.offlineMode,
    });
  }

  create(): void {
    console.log('GameScene created - Paper.io Style');

    // 게임 상태 초기화
    useGameStore.getState().resetGame();
    this.isGameOver = false;
    this.localDeathCompleted = false;
    this.localDeathBroadcasted = false;
    this.localDeathBroadcastPromise = undefined;
    this.roomLeaveStarted = false;
    this.remoteCleanupTimer = 0;

    // 초기화
    this.usedColorIndices.clear();
    this.usedColors.clear();
    this.aiBots = [];
    this.remoteBots.clear();
    this.remotePlayers.clear();

    // 오디오 매니저 초기화
    this.audioManager = AudioManager.getInstance(this);
    this.audioManager.startBgm();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.audioManager?.stopMusic('game_bgm');
      this.unsubscribeStore?.();
      this.unsubscribeStore = undefined;
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        this.visibilityHandler = undefined;
      }
      if (!this.roomLeaveStarted) {
        void this.leaveRoom();
      }
      this.itemManager?.destroy();
      this.itemManager = undefined;
      this.missileManager?.destroy();
      this.missileManager = undefined;
      this.hideItemToast();
      this.setReverseWarning(false);
    });

    // 파티클 매니저 초기화
    this.particleManager = new ParticleManager(this);

    // 원형 월드맵 생성
    this.circularWorld = new CircularWorld(
      this,
      this.worldCenterX,
      this.worldCenterY,
      this.worldRadius
    );

    // 영역 관리 시스템 생성
    this.territoryManager = new TerritoryManager(
      this,
      this.worldCenterX,
      this.worldCenterY,
      this.worldRadius
    );

    // 아이템 매니저 생성 (스폰/만료는 호스트 권위)
    this.itemManager = new ItemManager(
      this,
      this.worldCenterX,
      this.worldCenterY,
      this.worldRadius
    );
    this.itemManager.setOnSpawn((items) => this.socketService?.sendItemSpawn(items));
    this.itemManager.setOnDespawn((ids) => this.socketService?.sendItemDespawn(ids));

    // 미사일 매니저 (폭탄 발사체)
    this.missileManager = new MissileManager(this);

    // 카메라 설정
    const worldSize = (this.worldRadius + 100) * 2;
    this.cameras.main.setBounds(0, 0, worldSize, worldSize);

    // 모바일에서는 더 넓은 시야 제공
    const isMobile = (window as any).isMobileDevice || false;
    this.cameras.main.setZoom(isMobile ? 0.55 : 0.8);

    // 플레이어 ID 설정 (서버에서 받은 ID 사용)
    useGameStore.getState().setLocalPlayerId(this.playerId);

    // 플레이어 생성
    this.createPlayer();

    // AI 봇 생성: 오프라인 모드는 즉시, 온라인 모드는 sticky 호스트 로직이 결정
    this.sceneStartAt = Date.now();
    this.knownHostId = undefined;
    // 0이면 모든 순위의 인계 대기 시간이 한꺼번에 지나버려 계단식이 무력화되므로
    // 씬 시작 시각 기준으로 초기화한다
    this.lastBotFeedAt = Date.now();
    this.lastPresentIds = [];
    if (this.isHost) {
      console.log('Offline mode - creating AI bots immediately');
      this.createAIBots(5);
    } else {
      console.log('Waiting for sticky host takeover conditions');
    }

    // 호스트 탭이 백그라운드로 가면 rAF가 멈춰 봇이 전부 얼어버리므로,
    // 숨겨지는 즉시 호스트를 사임해서 다른 클라이언트가 인계하게 한다.
    this.visibilityHandler = () => {
      if (document.hidden && this.isHost && !this.offlineMode && !this.isGameOver) {
        this.resignHost('tab hidden');
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // 주의: 과거에는 room-join 응답의 players 목록(DB 행)을 스토어에 등록했지만,
    // 탭을 그냥 닫은 세션의 is_alive=true 행이 영구히 남아 유령 플레이어가
    // 순위에 표시되는 원인이었다. 실제 접속자는 Presence가 채워준다.

    // 꼬리 단절 시 사망 콜백
    this.territoryManager?.setOnTrailCutCallback((playerId) => {
      this.handleTrailCut(playerId);
    });

    // 소켓 연결 (비호스트는 봇 이동도 수신)
    if (!this.offlineMode) {
      this.socketService = new SocketService(
        this.roomId,
        this.playerId,
        this.playSessionId,
        this.nickname,
        this.playerColor || '#4D96FF',
        (payload) => this.handleMapUpdate(payload),
        (payload) => this.handleBotMove(payload),
        (payload) => this.handleTerritoryUpdate(payload),
        (players) => this.handlePresenceSync(players),
        (killerId) => this.handleKilledByRemote(killerId),
        (payload) => this.handleTerritorySnapshot(payload),
        (payload) => this.handleBotKillRequest(payload),
        (payload) => this.handleBotRemove(payload)
      );
      this.socketService.setItemHandlers({
        onSpawn: (items) => this.itemManager?.applySpawn(items),
        onDespawn: (ids) => this.itemManager?.applyDespawn(ids),
        onSnapshot: (items) => this.itemManager?.applySnapshot(items),
        onCollect: (payload) => this.handleItemCollect(payload),
        onBombFire: (payload) =>
          this.missileManager?.spawnBurst(payload.owner_id, payload.x, payload.y, payload.color),
      });
      this.socketService.connect();
    } else {
      console.log('Offline game test mode - socket connection skipped');
    }

    // 스토어 구독
    this.unsubscribeStore = useGameStore.subscribe((state) => {
      this.syncRemotePlayers(state.players);
    });

    // UI Scene 시작
    this.scene.launch('UIScene');
  }

  /**
   * 사용되지 않은 고유 색상 랜덤하게 가져오기
   */
  private getUniqueColor(): number {
    // 1. 팔레트에서 사용 가능한 색상 인덱스 수집
    const availableIndices: number[] = [];
    for (let i = 0; i < COLOR_PALETTE.length; i++) {
      if (!this.usedColorIndices.has(i)) {
        availableIndices.push(i);
      }
    }

    // 2. 사용 가능한 색상 중 랜덤 선택
    if (availableIndices.length > 0) {
      const randomIdx = availableIndices[Math.floor(Math.random() * availableIndices.length)];
      this.usedColorIndices.add(randomIdx);
      this.usedColors.add(COLOR_PALETTE[randomIdx]);
      return COLOR_PALETTE[randomIdx];
    }

    // 3. 팔레트 소진 시 고유한 랜덤 색상 생성
    let attempts = 0;
    const maxAttempts = 100;

    while (attempts < maxAttempts) {
      // HSV 기반으로 선명한 색상 생성 (채도와 명도 높게)
      const hue = Math.random() * 360;
      const saturation = 0.7 + Math.random() * 0.3; // 70-100%
      const value = 0.7 + Math.random() * 0.3; // 70-100%

      const colorObj = Phaser.Display.Color.HSVToRGB(hue / 360, saturation, value) as Phaser.Display.Color;
      const colorValue = colorObj.color;

      // 이미 사용 중인 색상과 비교
      if (!this.isColorTooSimilar(colorValue)) {
        this.usedColors.add(colorValue);
        return colorValue;
      }

      attempts++;
    }

    // 최후의 수단: 랜덤 색상 반환
    const fallbackColor = Phaser.Display.Color.RandomRGB().color;
    this.usedColors.add(fallbackColor);
    return fallbackColor;
  }

  /**
   * 색상이 기존 색상들과 너무 유사한지 확인
   */
  private isColorTooSimilar(newColor: number): boolean {
    const threshold = 50; // 색상 차이 임계값

    for (const usedColor of this.usedColors) {
      const r1 = (newColor >> 16) & 0xFF;
      const g1 = (newColor >> 8) & 0xFF;
      const b1 = newColor & 0xFF;

      const r2 = (usedColor >> 16) & 0xFF;
      const g2 = (usedColor >> 8) & 0xFF;
      const b2 = usedColor & 0xFF;

      // 유클리드 거리 계산
      const distance = Math.sqrt(
        Math.pow(r1 - r2, 2) +
        Math.pow(g1 - g2, 2) +
        Math.pow(b1 - b2, 2)
      );

      if (distance < threshold) {
        return true; // 너무 유사함
      }
    }

    return false;
  }

  /**
   * AI 봇 생성 (결정론적 - 모든 클라이언트에서 동일)
   * seeds가 주어지면(호스트 인계 시) 원격 봇의 마지막 위치/색/이름을 이어받는다.
   */
  private createAIBots(count: number, seeds?: Map<string, Enemy>): void {
    const botCount = Math.min(count, FIXED_BOT_DATA.length);
    const fixedDistance = this.worldRadius * 0.6; // 중앙에서 60% 거리

    for (let i = 0; i < botCount; i++) {
      const botData = FIXED_BOT_DATA[i];
      const id = `bot_${i}`;

      // 호스트 재승격 등으로 이미 존재하는 봇은 중복 생성하지 않음
      if (this.aiBots.some((existing) => existing.id === id)) continue;

      const seed = seeds?.get(id);

      // 결정론적 위치 (angleOffset 기반), 인계 시에는 원격 봇의 마지막 위치
      const angle = (botData.angleOffset * Math.PI) / 180;
      const x = seed ? seed.x : this.worldCenterX + Math.cos(angle) * fixedDistance;
      const y = seed ? seed.y : this.worldCenterY + Math.sin(angle) * fixedDistance;

      let color: number;
      let name: string;
      if (seed) {
        color = seed.getColor();
        name = seed.getNickname();
      } else {
        // 색상 팔레트에서 고정된 인덱스 사용
        color = COLOR_PALETTE[botData.colorIndex % COLOR_PALETTE.length];
        this.usedColorIndices.add(botData.colorIndex % COLOR_PALETTE.length);
        // 초기 봇 이름도 고정값 대신 랜덤 (현재 사용 중인 이름과 중복 회피)
        name = this.pickBotName();
      }

      console.log(`Creating bot ${id}: ${name} at (${x.toFixed(0)}, ${y.toFixed(0)}) color: ${color.toString(16)}`);

      const bot = new AIBot(
        this,
        x, y,
        color,
        id,
        name,
        this.worldCenterX,
        this.worldCenterY,
        this.worldRadius
      );

      if (this.territoryManager) {
        bot.setTerritoryManager(this.territoryManager);
        // 호스트 인계 시 기존 영역이 이미 있으므로 새로 만들지 않는다
        if (!this.territoryManager.getTerritory(id)) {
          this.territoryManager.createInitialTerritory(id, x, y, color, 80);
        }
      }

      if (this.particleManager) {
        bot.setParticleManager(this.particleManager);
      }

      // 공격성은 인덱스 기반으로 동적 배정 (applyBotComposition이 재조정)
      bot.setAggressive(i < this.desiredBotComposition().aggressive);

      // 봇 사망 시 리스폰
      bot.setOnDeathCallback(() => {
        this.handleBotDeath(bot);
      });

      // 봇 영역 확장을 다른 클라이언트에도 동기화
      // (이전에는 호스트 로컬에서만 확장되어 클라이언트마다 봇 영역이 다르게 보였음)
      bot.setOnTerritoryCompleteCallback((trailPoints) => {
        this.socketService?.sendTerritoryUpdate(trailPoints, bot.getColor(), bot.id);
        this.handleBotPickup(bot);
      });

      this.aiBots.push(bot);
    }
  }

  /** 호스트: 봇이 이번 점령으로 감싼 아이템 획득 (플레이어와 동일 규칙) */
  private handleBotPickup(bot: AIBot): void {
    if (!this.itemManager || !this.territoryManager) return;
    const picked = this.itemManager.checkPickup(this.territoryManager, bot.id);
    if (picked.length === 0) return;

    const { effect, bomb } = resolvePickup(rollMysteryTypes(picked.map((p) => p.type)));
    const ids = picked.map((p) => p.id);
    this.itemManager.removeItems(ids);
    this.socketService?.sendItemCollect({
      collector_id: bot.id,
      collected_ids: ids,
      effect,
      bomb,
    });

    if (effect) bot.applyEffect(effect);
    if (bomb) this.fireBomb(bot.id, bot.x, bot.y, bot.getColor());
  }

  /**
   * 봇 이름 선택 (한글 풀 전용, 현재 사용 중인 이름과 중복 회피)
   */
  private pickBotName(): string {
    const used = new Set(this.aiBots.map((b) => b.getNickname()));
    const available = BOT_NAMES_KR.filter((n) => !used.has(n));
    const pool = available.length > 0 ? available : BOT_NAMES_KR;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /**
   * 꼬리 단절로 인한 사망
   */
  private handleTrailCut(playerId: string): void {
    const localId = useGameStore.getState().localPlayerId;

    if (playerId === localId && this.localPlayer) {
      console.log('Trail cut - player died!');
      useGameStore.getState().setDeathCause('꼬리가 잘려서 탈락했어요!');
      this.particleManager?.explode(this.localPlayer.x, this.localPlayer.y, this.localPlayer.getColor());
      this.handleSelfCollision();
      return;
    }

    const bot = this.aiBots.find(b => b.id === playerId);
    if (bot) {
      console.log(`Trail cut - bot ${playerId} died!`);
      this.handleBotDeath(bot);
    }
  }

  /**
   * 봇 사망 처리
   */
  private handleBotDeath(bot: AIBot): void {
    // 같은 봇에 대한 중복 사망 처리 방지 (연속 충돌 프레임, 원격 킬 요청 중복 등)
    const nowMs = Date.now();
    if ((this.lastBotDeathAt.get(bot.id) ?? 0) + 1000 > nowMs) return;
    this.lastBotDeathAt.set(bot.id, nowMs);

    console.log(`Bot ${bot.id} died!`);
    this.particleManager?.explode(bot.x, bot.y, bot.getColor());

    // 영역 파괴 효과 (리셋 전에 영역 정보 가져오기)
    const territory = this.territoryManager?.getTerritory(bot.id);
    if (territory && this.particleManager) {
      this.particleManager.territoryShatter(territory.points, bot.getColor());
    }

    // 기존 색상 해제
    this.usedColors.delete(bot.getColor());

    // 새로운 랜덤 색상과 닉네임 할당
    // (공격형 봇도 일반 봇과 구분되지 않게 표시한다)
    const newColor = this.getUniqueColor();
    const newNickname = this.pickBotName();
    bot.resetIdentity(newColor, newNickname);

    // 안전한 리스폰 위치 찾기 (영역이 없는 곳)
    const respawnPos = this.territoryManager?.findSafeRespawnPosition(
      this.worldCenterX,
      this.worldCenterY,
      this.worldRadius
    ) || { x: this.worldCenterX, y: this.worldCenterY };

    bot.respawn(respawnPos.x, respawnPos.y);

    if (this.territoryManager) {
      this.territoryManager.resetTerritory(bot.id, respawnPos.x, respawnPos.y, newColor, 80);
    }
  }

  private handleMapUpdate(payload: any): void {
    if (!this.circularWorld) return;

    const tiles = payload.tiles || [];
    let shouldShake = false;

    tiles.forEach((tile: any) => {
      if (this.localPlayer && !shouldShake) {
        const dist = Phaser.Math.Distance.Between(
          tile.x * GAME_CONSTANTS.map.tileSize,
          tile.y * GAME_CONSTANTS.map.tileSize,
          this.localPlayer.x,
          this.localPlayer.y
        );
        if (dist < 500) shouldShake = true;
      }
    });

    if (shouldShake) {
      this.cameras.main.shake(200, 0.005);
    }

    const uiScene = this.scene.get('UIScene') as any;
    if (uiScene && uiScene.updateMinimap) {
      uiScene.updateMinimap(tiles);
    }
  }

  /**
   * 호스트로부터 봇 이동 수신 (비호스트 전용)
   */
  private handleBotMove(payload: {
    bot_id: string;
    host_id?: string;
    x: number;
    y: number;
    direction: number;
    trail?: { x: number; y: number }[];
    effect?: { type: import('../config/items').ItemType; remainingMs: number } | null;
    nickname: string;
    color: number;
  }): void {
    if (payload.host_id === this.playerId) return;

    if (this.isHost) {
      // 호스트 중복 감지: 더 작은 ID가 호스트를 유지한다.
      // (상대도 같은 규칙으로 내 bot:move를 받으면 사임하므로 한쪽으로 수렴)
      if (payload.host_id && payload.host_id < this.playerId) {
        this.resignHost(`duplicate host detected, yielding to ${payload.host_id}`);
        // 사임 후 아래에서 원격 봇으로 처리
      } else {
        return;
      }
    }

    if (this.knownHostId !== payload.host_id) {
      console.log('Bot host is now:', payload.host_id);
      this.knownHostId = payload.host_id;
    }
    this.lastBotFeedAt = Date.now();

    // 로컬 예측으로 막 죽인 봇의 잔여 패킷(옛 색/이름)은 무시 — 좀비 깜빡임 방지.
    // 새 정체성 패킷(=호스트의 리스폰 반영)이 오면 억제를 풀고 정상 생성한다.
    const suppress = this.botKillSuppress.get(payload.bot_id);
    if (suppress) {
      if (
        Date.now() < suppress.until &&
        payload.color === suppress.color &&
        payload.nickname === suppress.nickname
      ) {
        return;
      }
      this.botKillSuppress.delete(payload.bot_id);
    }

    let remoteBot = this.remoteBots.get(payload.bot_id);

    // 봇이 호스트 쪽에서 죽고 새 색/이름으로 리스폰한 경우:
    // 기존 오브젝트와 영역을 버리고 새로 만든다 (이전에는 옛 색/이름/영역이 그대로 남았음)
    if (
      remoteBot &&
      (remoteBot.getColor() !== payload.color || remoteBot.getNickname() !== payload.nickname)
    ) {
      remoteBot.clearTrail();
      remoteBot.destroy();
      this.remoteBots.delete(payload.bot_id);
      this.territoryManager?.removePlayerTerritory(payload.bot_id);
      remoteBot = undefined;
    }

    if (!remoteBot) {
      // 새 봇 생성
      remoteBot = new Enemy(
        this,
        payload.x,
        payload.y,
        payload.color,
        payload.bot_id,
        payload.nickname
      );
      this.remoteBots.set(payload.bot_id, remoteBot);

      // 봇 영역 생성 (호스트 인계 직후에는 기존 영역이 있으므로 중복 생성 방지)
      if (this.territoryManager && !this.territoryManager.getTerritory(payload.bot_id)) {
        this.territoryManager.createInitialTerritory(
          payload.bot_id,
          payload.x,
          payload.y,
          payload.color,
          80
        );
      }

      console.log('Created remote bot:', payload.nickname);
    }

    // 봇 상태 업데이트
    remoteBot.updateState(payload.x, payload.y, payload.trail, payload.direction, 'world');
    remoteBot.applyRemoteEffect(payload.effect);

    // 봇 캐릭터 위치 업데이트 (영역 분할 시 사용)
    this.territoryManager?.updateCharacterPosition(payload.bot_id, payload.x, payload.y);
  }

  /**
   * 다른 플레이어의 영역 확장 수신
   */
  private handleTerritoryUpdate(payload: {
    user_id: string;
    trail_points: { x: number; y: number }[];
    color: number;
  }): void {
    if (!this.territoryManager) return;

    // 죽거나 떠난 플레이어의 늦게 도착한 확장 패킷은 무시 (영역 부활 방지)
    if (this.socketService?.isPlayerTombstoned(payload.user_id)) {
      if (this.territoryManager.getTerritory(payload.user_id)) {
        this.territoryManager.removePlayerTerritory(payload.user_id);
      }
      return;
    }

    console.log('Received territory update from:', payload.user_id);

    // 해당 플레이어의 영역이 없으면 생성
    if (!this.territoryManager.getTerritory(payload.user_id)) {
      // 트레일 포인트의 중심에 초기 영역 생성
      let cx = 0, cy = 0;
      for (const p of payload.trail_points) {
        cx += p.x;
        cy += p.y;
      }
      cx /= payload.trail_points.length || 1;
      cy /= payload.trail_points.length || 1;

      this.territoryManager.createInitialTerritory(
        payload.user_id,
        cx,
        cy,
        payload.color,
        80
      );
    }

    // 영역 확장 적용
    this.territoryManager.expandTerritory(payload.user_id, payload.trail_points);
  }

  /**
   * Presence 동기화 처리.
   * 플레이어 스토어 반영은 소켓에서 처리하고, 여기서는 접속자 목록만 기록한다.
   * 호스트 인계 판단은 update()의 sticky 호스트 로직이 담당한다
   * (입장 즉시 선출하면 봇 상태를 받기 전에 호스트를 빼앗아 봇이 리셋되는 문제가 있었음).
   */
  private handlePresenceSync(players: Map<string, any>): void {
    if (this.offlineMode || this.isGameOver) return;

    const previousIds = new Set(this.lastPresentIds);
    this.lastPresentIds = [this.playerId, ...players.keys()].sort();
    console.log('Presence sync - present clients:', this.lastPresentIds.length);

    // 새 플레이어가 입장하면 내 영역(호스트면 봇 영역 포함) 스냅샷을 보내준다.
    // 영역은 증분(territory:update)으로만 동기화되므로, 스냅샷 없이는
    // 늦게 입장한 클라이언트가 기존 영역을 볼 수 없다.
    // 봇 호스트가 방을 떠났으면 인계 대기 시간을 앞당긴다
    // (계단식 순위는 유지하되, 1순위는 즉시 인계 가능)
    if (
      this.knownHostId &&
      this.knownHostId !== this.playerId &&
      !this.lastPresentIds.includes(this.knownHostId)
    ) {
      console.log('Bot host left the room:', this.knownHostId);
      this.knownHostId = undefined;
      this.lastBotFeedAt = Math.min(this.lastBotFeedAt, Date.now() - this.BOT_FEED_STALE_MS);
    }

    // 신규 입장자 자신의 첫 sync에서도 발동된다 (기존 플레이어들이 "새로" 보이므로)
    // → 양방향 모두 스냅샷이 교환된다: 기존 영역 → 신규, 신규 시작 원 → 기존.
    const hasNewcomer = this.lastPresentIds.some(
      (id) => id !== this.playerId && !previousIds.has(id)
    );
    if (hasNewcomer) {
      // 모든 클라이언트가 동시에 쏟아내지 않도록 약간의 랜덤 지연
      this.time.delayedCall(300 + Math.random() * 900, () => {
        this.broadcastTerritorySnapshots();
        // 아이템은 호스트만 권위를 가지므로 호스트가 현재 목록 스냅샷 전송
        if (this.isHost && this.itemManager) {
          this.socketService?.sendItemSnapshot(this.itemManager.getSnapshot());
        }
      });
    }

    // 접속자 수 변동에 맞춰 봇 구성(수/공격성) 재조정
    this.applyBotComposition();
  }

  /**
   * 내 영역(+호스트라면 봇 영역) 전체 스냅샷 브로드캐스트
   */
  private broadcastTerritorySnapshots(): void {
    if (this.offlineMode || this.isGameOver || !this.territoryManager || !this.socketService) return;

    const mySnapshot = this.territoryManager.getTerritorySnapshot(this.playerId);
    if (mySnapshot) {
      this.socketService.sendTerritorySnapshot(this.playerId, mySnapshot.color, mySnapshot.polygon);
    }

    if (this.isHost) {
      for (const bot of this.aiBots) {
        const botSnapshot = this.territoryManager.getTerritorySnapshot(bot.id);
        if (botSnapshot) {
          this.socketService.sendTerritorySnapshot(bot.id, botSnapshot.color, botSnapshot.polygon);
        }
      }
    }
  }

  /**
   * 다른 클라이언트가 보낸 영역 스냅샷 적용 (소유자 상태가 권위)
   */
  private handleTerritorySnapshot(payload: {
    user_id: string;
    color: number;
    polygon: [number, number][][][];
  }): void {
    if (!this.territoryManager) return;
    if (payload.user_id === this.playerId) return;
    // 내가 봇 호스트면 봇 영역은 내 시뮬레이션이 권위 (떠난 옛 호스트의 늦은 패킷 무시)
    if (this.isHost && payload.user_id.startsWith('bot_')) return;

    // 죽거나 떠난 플레이어의 늦게 도착한 스냅샷은 무시 (영역 부활 방지)
    // 재입장한 경우에는 새 세션의 이동 패킷이 tombstone을 먼저 해제하므로 막히지 않는다.
    if (this.socketService?.isPlayerTombstoned(payload.user_id)) {
      if (this.territoryManager.getTerritory(payload.user_id)) {
        this.territoryManager.removePlayerTerritory(payload.user_id);
      }
      return;
    }

    console.log('Applied territory snapshot from:', payload.user_id);
    this.territoryManager.setTerritorySnapshot(payload.user_id, payload.color, payload.polygon);
  }

  /**
   * Sticky 호스트 인계 판단 (update 루프에서 호출):
   * 봇 피드(bot:move)가 살아 있는 동안에는 절대 호스트를 빼앗지 않는다.
   *
   * 인계 자격은 "가장 작은 ID 한 명"이 아니라 순위 기반 계단식이다:
   * 1순위는 피드 정지 3초 후, 2순위는 4.5초 후, 3순위는 6초 후...
   * 1순위 클라이언트의 탭이 백그라운드면 rAF가 멈춰 인계 코드 자체가 실행되지
   * 않으므로, 그 한 명에게만 자격을 주면 봇이 영원히 멈추는 문제가 있었다.
   * 둘이 동시에 인계해도 bot:move의 host_id 비교(작은 ID 승)로 수렴한다.
   */
  private evaluateHostTakeover(): void {
    if (this.isHost || this.offlineMode || this.isGameOver) return;

    const now = Date.now();

    if (this.lastPresentIds.length === 0) {
      // Presence 자체가 한 번도 안 왔으면(연결 문제) 단독 호스트로 폴백
      if (now - this.sceneStartAt > this.NO_PRESENCE_FALLBACK_MS) {
        this.becomeHost('presence unavailable fallback');
      }
      return;
    }

    const myRank = this.lastPresentIds.indexOf(this.playerId);
    if (myRank === -1) return;

    const staleThreshold = this.BOT_FEED_STALE_MS + myRank * 1500;
    if (now - this.lastBotFeedAt < staleThreshold) return;

    const solo = this.lastPresentIds.length === 1;
    // 입장 직후에는 기존 호스트의 첫 bot:move가 도착할 시간을 준다
    const matured = now - this.sceneStartAt > this.BOT_FEED_STALE_MS;
    if (solo || matured) {
      this.becomeHost(solo ? 'solo player' : `host feed stale (rank ${myRank})`);
    }
  }

  /**
   * 접속자 수에 따른 봇 구성 결정.
   * 사용자가 늘수록 일반 봇을 줄이고 공격형 봇 위주로 배치한다.
   * 1명: 공1+일반4 / 2명: 공2+일반3 / 3명: 공3+일반2 / 4명: 공3+일반1 / 5명+: 공3
   */
  private desiredBotComposition(): { total: number; aggressive: number } {
    const players = Math.max(1, this.lastPresentIds.length);
    const aggressive = Math.min(MAX_AGGRESSIVE_BOTS, players);
    const normal = Math.max(0, FIXED_BOT_DATA.length - players);
    return {
      total: Math.min(FIXED_BOT_DATA.length, aggressive + normal),
      aggressive,
    };
  }

  /**
   * 현재 접속자 수에 맞게 봇 수/공격성을 재조정 (호스트 전용)
   */
  private applyBotComposition(): void {
    if (!this.isHost || this.offlineMode || this.isGameOver) return;

    const { total, aggressive } = this.desiredBotComposition();

    // 부족하면 생성 (createAIBots는 이미 있는 인덱스를 건너뛴다)
    if (this.aiBots.length < total) {
      this.createAIBots(total);
    }

    // 초과분 제거 + 공격성 재배정
    for (const bot of [...this.aiBots]) {
      const index = Number(bot.id.split('_')[1]);
      if (index >= total) {
        this.removeBot(bot);
        continue;
      }
      bot.setAggressive(index < aggressive);
      if (index >= aggressive) {
        bot.setHuntTarget(null);
      }
    }

    // 호스트 인계 직전 구성이 더 컸던 경우 남은 영역/원격 표시 정리
    for (let i = total; i < FIXED_BOT_DATA.length; i++) {
      const id = `bot_${i}`;
      if (this.territoryManager?.getTerritory(id)) {
        this.territoryManager.removePlayerTerritory(id);
        this.socketService?.sendBotRemove(id);
      }
    }
  }

  /**
   * 봇을 게임에서 제거 (구성 축소 시) - 다른 클라이언트에도 제거를 알린다
   */
  private removeBot(bot: AIBot): void {
    console.log(`Removing bot ${bot.id} (composition change)`);
    this.aiBots = this.aiBots.filter((b) => b !== bot);
    this.usedColors.delete(bot.getColor());
    bot.destroy();
    this.territoryManager?.removePlayerTerritory(bot.id);
    this.socketService?.sendBotRemove(bot.id);
  }

  /**
   * 호스트가 보낸 봇 제거 알림 처리 (비호스트)
   */
  private handleBotRemove(payload: { bot_id: string }): void {
    if (this.isHost) return;

    const remoteBot = this.remoteBots.get(payload.bot_id);
    if (remoteBot) {
      remoteBot.clearTrail();
      remoteBot.destroy();
      this.remoteBots.delete(payload.bot_id);
    }
    this.territoryManager?.removePlayerTerritory(payload.bot_id);
    this.botKillSuppress.delete(payload.bot_id);
    console.log('Remote bot removed by host:', payload.bot_id);
  }

  /**
   * 봇 호스트로 승격: 원격 봇이 있으면 그 상태(위치/색/이름)를 이어받아 로컬 봇 생성
   */
  private becomeHost(reason: string): void {
    if (this.isHost) return;
    console.log(`Becoming bot host (${reason})`);

    this.isHost = true;
    this.knownHostId = this.playerId;

    const seeds = this.remoteBots;
    this.createAIBots(this.desiredBotComposition().total, seeds);

    seeds.forEach((bot) => bot.destroy());
    this.remoteBots = new Map();

    // 구성(공격성/봇 수)을 현재 접속자 수 기준으로 정렬
    this.applyBotComposition();
  }

  /**
   * 봇 호스트 사임: 로컬 봇을 제거하고 다른 호스트의 bot:move 브로드캐스트를 따른다.
   * 영역은 봇 ID 기준으로 유지되므로 새 호스트가 같은 ID로 이어받는다.
   */
  private resignHost(reason: string): void {
    if (!this.isHost) return;
    console.log('Resigning bot host:', reason);

    this.isHost = false;
    this.knownHostId = undefined;
    // 사임 직후 자기 자신이 곧바로 다시 인계하지 않도록 피드 시각을 리셋
    this.lastBotFeedAt = Date.now();
    for (const bot of this.aiBots) {
      bot.destroy();
    }
    this.aiBots = [];
  }

  private syncRemotePlayers(playersData: Map<string, any>): void {
    const gameState = useGameStore.getState();
    const localId = gameState.localPlayerId;
    const now = Date.now();

    // 떠난 플레이어 제거
    this.remotePlayers.forEach((_remotePlayer, id) => {
      if (!playersData.has(id)) {
        this.removeRemotePlayer(id, 'left');
      }
    });

    // 플레이어 추가/업데이트
    playersData.forEach((data, id) => {
      if (id === localId) return;

      // 사망한 플레이어 처리
      if (!data.isAlive) {
        this.removeRemotePlayer(id, 'dead');
        return;
      }

      if (!data.hasPosition) {
        this.removeRemotePlayer(id, 'waiting-for-position', false);
        return;
      }

      if (
        data.lastSeenAt &&
        now - data.lastSeenAt > GAME_CONSTANTS.network.remoteStaleTimeout
      ) {
        // 일시적 끊김일 수 있으므로 캐릭터만 숨기고 영역은 남긴다
        // (죽거나 떠난 경우는 death/leave 이벤트가 영역까지 제거)
        this.removeRemotePlayer(id, 'stale', false);
        return;
      }

      let remotePlayer = this.remotePlayers.get(id);
      if (!remotePlayer) {
        const color = Phaser.Display.Color.HexStringToColor(data.color).color;
        remotePlayer = new Enemy(
          this,
          data.x,
          data.y,
          color,
          id,
          data.nickname || 'Unknown'
        );
        this.remotePlayers.set(id, remotePlayer);
      }

      remotePlayer.updateState(data.x, data.y, data.trail, data.direction, data.trailSpace ?? 'grid');
      remotePlayer.applyRemoteEffect(data.effect);
    });
  }

  private removeRemotePlayer(
    id: string,
    reason: string,
    removeTerritory: boolean = true
  ): void {
    const remotePlayer = this.remotePlayers.get(id);

    if (remotePlayer) {
      remotePlayer.clearTrail();
      remotePlayer.destroy();
      this.remotePlayers.delete(id);
      console.log(`Removed remote player (${reason}):`, id);
    }

    // 캐릭터 오브젝트가 이미 없어도 영역은 제거해야 한다
    // (과거에는 오브젝트가 없으면 조기 반환해서 영역만 화면에 남는 경우가 있었음)
    if (removeTerritory && this.territoryManager?.getTerritory(id)) {
      this.territoryManager.removePlayerTerritory(id);
      if (!remotePlayer) {
        console.log(`Removed orphan territory (${reason}):`, id);
      }
    }
  }

  private createPlayer(): void {
    // 서버에서 받은 스폰 위치 사용, 없으면 안전한 위치 찾기
    let startX: number;
    let startY: number;

    if (this.spawnPosition) {
      // 서버에서 받은 스폰 위치를 월드 좌표로 변환 (그리드 -> 월드)
      startX = this.worldCenterX + (this.spawnPosition.x - 50) * 50;
      startY = this.worldCenterY + (this.spawnPosition.y - 50) * 50;
    } else if (this.territoryManager) {
      const safePos = this.territoryManager.findSafeRespawnPosition(
        this.worldCenterX,
        this.worldCenterY,
        this.worldRadius
      );
      startX = safePos.x;
      startY = safePos.y;
    } else {
      // 랜덤 위치
      const angle = Math.random() * Math.PI * 2;
      const minDist = 300;
      const maxDist = this.worldRadius - 400;
      const dist = minDist + Math.random() * (maxDist - minDist);
      startX = this.worldCenterX + Math.cos(angle) * dist;
      startY = this.worldCenterY + Math.sin(angle) * dist;
    }

    // 서버에서 받은 색상 사용, 없으면 랜덤 색상
    let playerColor: number;
    if (this.playerColor) {
      playerColor = Phaser.Display.Color.HexStringToColor(this.playerColor).color;
    } else {
      playerColor = this.getUniqueColor();
    }

    const localId = this.playerId || useGameStore.getState().localPlayerId || 'unknown';

    useGameStore.getState().updatePlayer(localId, { nickname: this.nickname });

    this.localPlayer = new Player(
      this,
      startX,
      startY,
      playerColor,
      localId,
      this.nickname,
      true
    );

    if (this.territoryManager) {
      this.localPlayer.setTerritoryManager(this.territoryManager);
      this.territoryManager.createInitialTerritory(localId, startX, startY, playerColor, 80);
    }

    this.localPlayer.setWorldBounds(this.worldCenterX, this.worldCenterY, this.worldRadius);

    if (this.particleManager) {
      this.localPlayer.setParticleManager(this.particleManager);
    }

    this.localPlayer.setOnDeathCallback(() => {
      this.handleSelfCollision();
    });

    // 영역 확장 시 다른 플레이어에게 브로드캐스트 + 감싼 아이템 획득 판정
    this.localPlayer.setOnTerritoryCompleteCallback((trailPoints) => {
      this.audioManager?.play('conquer');
      this.socketService?.sendTerritoryUpdate(trailPoints, playerColor);
      this.socketService?.sendTrailClear();
      this.handleLocalPickup();
    });

    this.cameras.main.startFollow(this.localPlayer, true, 0.1, 0.1);

    // 스폰 보호: 시작 무적 + 영역 스냅샷 안정화 후 안전 위치 보정
    // (다른 플레이어 영역 위에 스폰되면 내 초기 영역이 grid 경쟁에서 덮여
    //  영역 밖 판정 → 첫 꼬리에서 즉사하던 문제 방지)
    this.localPlayer.startInvincibility(3000);
    if (!this.offlineMode) {
      this.time.delayedCall(1400, () => this.ensureSafeSpawn(playerColor));
    }
  }

  /**
   * 스폰 직후 검증: 내 캐릭터가 다른 플레이어 영역에 갇혀 내 영역이 없으면
   * 빈 공간으로 재배치하고 초기 영역을 다시 만든다.
   */
  private ensureSafeSpawn(playerColor: number): void {
    if (!this.localPlayer || !this.territoryManager || this.isGameOver) return;
    const p = this.localPlayer;

    // 내 영역이 멀쩡히 남아 있으면 정상 (꼬리를 그리며 나가 있는 중일 수도 있음).
    // 다른 플레이어 영역에 덮여 내 초기 영역이 소실됐을 때만 면적이 거의 0이 된다.
    const myArea = this.territoryManager.getTerritoryArea(p.id);
    if (myArea > 5000) return;

    console.log('Spawn overlapped another territory — relocating to safe spot');
    const safe = this.territoryManager.findSafeRespawnPosition(
      this.worldCenterX,
      this.worldCenterY,
      this.worldRadius
    );
    p.setPosition(safe.x, safe.y);
    p.resetTrail();
    this.territoryManager.removePlayerTerritory(p.id);
    this.territoryManager.createInitialTerritory(p.id, safe.x, safe.y, playerColor, 80);

    // 새 초기 영역을 다른 클라이언트에도 즉시 알림
    const snap = this.territoryManager.getTerritorySnapshot(p.id);
    if (snap) {
      this.socketService?.sendTerritorySnapshot(p.id, snap.color, snap.polygon);
    }
  }

  /**
   * 로컬 플레이어가 이번 점령으로 감싼 아이템 획득 판정.
   * 동시에 여러 개를 감싸면 우선순위로 지속효과 1개 + 폭탄(있으면)만 발동.
   * (효과 적용은 단계 2/3, 현재는 제거 + 동기화 + 토스트)
   */
  private handleLocalPickup(): void {
    if (!this.itemManager || !this.territoryManager || !this.localPlayer) return;
    const picked = this.itemManager.checkPickup(this.territoryManager, this.localPlayer.id);
    if (picked.length === 0) return;

    const { effect, bomb } = resolvePickup(rollMysteryTypes(picked.map((p) => p.type)));
    const ids = picked.map((p) => p.id);
    this.itemManager.removeItems(ids);
    this.socketService?.sendItemCollect({
      collector_id: this.playerId,
      collected_ids: ids,
      effect,
      bomb,
    });

    if (effect) {
      this.localPlayer.applyEffect(effect);
      this.audioManager?.play('rank_up');
      const msg = ITEM_TOAST[effect];
      if (msg) this.showItemToast(msg);
    }
    if (bomb) {
      this.fireBomb(this.localPlayer.id, this.localPlayer.x, this.localPlayer.y, this.localPlayer.getColor());
    }
  }

  /**
   * 미사일이 내 캐릭터(머리/꼬리)에 맞았는지, 호스트면 봇에도 판정.
   * 발사자 본인·무적은 면역.
   */
  private checkMissileHits(): void {
    if (!this.missileManager) return;

    // 로컬 플레이어
    const p = this.localPlayer;
    if (p && !this.isGameOver && !p.isInvincible) {
      let killer = this.missileManager.hitsAnyPoint([{ x: p.x, y: p.y }], 18, p.id);
      if (!killer && p.isTrailActive()) {
        killer = this.missileManager.hitsAnyPoint(p.getTrailPoints(), 14, p.id);
      }
      if (killer) {
        useGameStore.getState().setDeathCause(`${this.resolveKillerName(killer)}의 폭탄에 맞았어요!`);
        this.handleSelfCollision();
        return;
      }
    }

    // 봇 (호스트 권위)
    if (this.isHost) {
      for (const bot of this.aiBots) {
        if (bot.isInvincibleNow?.()) continue;
        let hit = this.missileManager.hitsAnyPoint([{ x: bot.x, y: bot.y }], 18, bot.id);
        if (!hit && bot.isTrailActive()) {
          hit = this.missileManager.hitsAnyPoint(bot.getTrailPoints(), 14, bot.id);
        }
        if (hit) {
          this.particleManager?.explode(bot.x, bot.y, bot.getColor());
          this.handleBotDeath(bot);
        }
      }
    }
  }

  /** 폭탄 발사: 로컬 미사일 생성 + 폭발 이펙트 + 동기화 브로드캐스트 */
  private fireBomb(ownerId: string, x: number, y: number, color: number): void {
    this.audioManager?.play('death'); // 폭발음 대용
    this.particleManager?.explode(x, y, color);
    this.missileManager?.spawnBurst(ownerId, x, y, color);
    this.socketService?.sendBombFire({ owner_id: ownerId, x: Math.round(x), y: Math.round(y), color });
  }

  /**
   * 원격 플레이어/봇이 아이템을 먹었다는 알림 처리.
   * 모든 클라이언트가 해당 아이템을 제거한다. (효과 적용은 단계 2)
   */
  private handleItemCollect(payload: ItemCollectPayload): void {
    // 아이템 제거만 담당한다.
    // 효과는 player:move/bot:move의 effect 필드로, 폭탄은 bomb:fire로 별도 동기화된다.
    this.itemManager?.removeItems(payload.collected_ids);
  }

  /**
   * 아이템 효과 설명 토스트 — 화면 상단에 잠깐 떴다 서서히 사라진다.
   * 게임을 가리지 않도록 상단·반투명·클릭 통과.
   */
  private itemToastEl?: HTMLDivElement;
  private itemToastTimer?: number;
  private showItemToast(message: string): void {
    this.hideItemToast();
    const el = document.createElement('div');
    el.textContent = message;
    el.style.cssText = `
      position: fixed;
      top: 12%;
      left: 50%;
      transform: translateX(-50%) translateY(-6px);
      z-index: 1050;
      padding: 9px 18px;
      background: rgba(34, 62, 80, 0.82);
      color: #FFFFFF;
      font-family: Nunito, sans-serif;
      font-size: 15px;
      font-weight: 800;
      border-radius: 999px;
      box-shadow: 0 4px 14px rgba(0,33,66,0.24);
      opacity: 0;
      transition: opacity 0.25s ease, transform 0.25s ease;
      pointer-events: none;
      white-space: nowrap;
      max-width: 92vw;
      overflow: hidden;
      text-overflow: ellipsis;
    `;
    document.body.appendChild(el);
    this.itemToastEl = el;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateX(-50%) translateY(0)';
    });
    this.itemToastTimer = window.setTimeout(() => {
      el.style.opacity = '0';
      el.style.transform = 'translateX(-50%) translateY(-6px)';
      window.setTimeout(() => {
        if (this.itemToastEl === el) this.itemToastEl = undefined;
        el.remove();
      }, 300);
    }, 2200);
  }

  private hideItemToast(): void {
    if (this.itemToastTimer !== undefined) {
      window.clearTimeout(this.itemToastTimer);
      this.itemToastTimer = undefined;
    }
    this.itemToastEl?.remove();
    this.itemToastEl = undefined;
  }

  /**
   * 역방향 효과 중 깜빡이는 경고 배너.
   * 역방향이면 꼬리를 만드는 순간 거의 죽으므로 "영역으로 복귀"를 강하게 안내한다.
   */
  private reverseWarningEl?: HTMLDivElement;
  private setReverseWarning(on: boolean): void {
    if (on && !this.reverseWarningEl) {
      const el = document.createElement('div');
      el.textContent = '🔄 역방향! 자신의 영역으로 복귀하세요!';
      el.style.cssText = `
        position: fixed;
        top: 21%;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1060;
        padding: 11px 22px;
        background: rgba(220, 53, 69, 0.92);
        color: #FFFFFF;
        font-family: Nunito, sans-serif;
        font-size: 16px;
        font-weight: 900;
        border-radius: 999px;
        box-shadow: 0 4px 16px rgba(150,0,20,0.4);
        pointer-events: none;
        white-space: nowrap;
        max-width: 92vw;
      `;
      document.body.appendChild(el);
      el.animate(
        [{ opacity: 1 }, { opacity: 0.25 }, { opacity: 1 }],
        { duration: 650, iterations: Infinity }
      );
      this.reverseWarningEl = el;
    } else if (!on && this.reverseWarningEl) {
      this.reverseWarningEl.remove();
      this.reverseWarningEl = undefined;
    }
  }

  private handleSelfCollision(): void {
    // 중복 사망 처리 방지
    if (!this.localPlayer || this.isGameOver) return;
    if (this.localPlayer.isInvincible) return; // 무적이면 사망 무시
    this.isGameOver = true;

    // 호출 경로에서 더 구체적인 원인(봇 이름 등)을 먼저 기록했으면 그것이 유지된다
    useGameStore.getState().setDeathCause('내 꼬리에 걸렸어요!');

    console.log('자기 꼬리와 충돌!');
    this.audioManager?.play('death');
    this.particleManager?.explode(this.localPlayer.x, this.localPlayer.y, this.localPlayer.getColor());

    // 영역 파괴 효과 (리셋 전에 영역 정보 가져오기)
    const territory = this.territoryManager?.getTerritory(this.localPlayer.id);
    if (territory && this.particleManager) {
      this.particleManager.territoryShatter(territory.points, this.localPlayer.getColor());
    }

    this.cameras.main.shake(300, 0.02);

    void this.broadcastLocalDeath(this.localPlayer.id, 'SELF_COLLISION');
    this.completeLocalDeath();
  }

  /**
   * 비호스트가 보낸 봇 사망 요청 처리 (봇 시뮬레이션 권위는 호스트)
   */
  private handleBotKillRequest(payload: { bot_id: string; killer_id: string }): void {
    if (!this.isHost) return;

    const bot = this.aiBots.find((b) => b.id === payload.bot_id);
    if (!bot) return;

    console.log(`Bot ${payload.bot_id} killed by remote player ${payload.killer_id}`);
    this.particleManager?.explode(bot.x, bot.y, bot.getColor());
    this.handleBotDeath(bot);
  }

  /**
   * 킬러 ID(플레이어 user_id 또는 bot_N)를 표시용 닉네임으로 변환
   */
  private resolveKillerName(killerId?: string): string {
    if (!killerId) return '상대 플레이어';

    if (killerId.startsWith('bot_')) {
      return (
        this.remoteBots.get(killerId)?.getNickname() ??
        this.aiBots.find((b) => b.id === killerId)?.getNickname() ??
        '봇'
      );
    }

    return (
      useGameStore.getState().players.get(killerId)?.nickname ??
      this.remotePlayers.get(killerId)?.getNickname() ??
      '상대 플레이어'
    );
  }

  /**
   * 다른 플레이어가 내 꼬리를 끊어 나를 죽인 경우 (player:death 브로드캐스트 수신)
   */
  private handleKilledByRemote(killerId?: string): void {
    if (!this.localPlayer || this.isGameOver) return;
    if (this.localPlayer.isInvincible) return; // 무적이면 원격 킬 무시
    this.isGameOver = true;

    useGameStore.getState().setDeathCause(
      `${this.resolveKillerName(killerId)}에게 꼬리를 잡혔어요!`
    );

    console.log('My trail was cut by:', killerId);
    this.audioManager?.play('death');
    this.particleManager?.explode(this.localPlayer.x, this.localPlayer.y, this.localPlayer.getColor());

    const territory = this.territoryManager?.getTerritory(this.localPlayer.id);
    if (territory && this.particleManager) {
      this.particleManager.territoryShatter(territory.points, this.localPlayer.getColor());
    }

    this.cameras.main.shake(300, 0.02);

    // 킬러가 이미 내 사망을 모두에게 브로드캐스트했으므로 중복 전송하지 않는다
    this.localDeathBroadcasted = true;
    this.completeLocalDeath();
  }

  private completeLocalDeath(): void {
    if (this.localDeathCompleted) return;
    this.localDeathCompleted = true;

    // 내가 봇 호스트였다면 즉시 사임 — isGameOver로 update()가 멈춰
    // 봇 시뮬레이션/브로드캐스트가 끊기므로, 다른 클라이언트가 빨리 인계하게 한다
    if (this.isHost && !this.offlineMode) {
      this.resignHost('local player died');
    }
    this.audioManager?.stopMusic('game_bgm');
    this.localPlayer?.resetTrail();
    const deathBroadcast = this.broadcastLocalDeath(
      useGameStore.getState().localPlayerId || undefined,
      'GAME_OVER'
    );

    useGameStore.getState().saveGameResult();
    void this.leaveRoom(deathBroadcast);

    this.time.delayedCall(1500, () => {
      this.scene.stop('UIScene');
      this.scene.start('LobbyScene');
    });
  }

  /**
   * 방에서 나가기 (사망 또는 퇴장 시)
   */
  private async leaveRoom(beforeDisconnect?: Promise<void>): Promise<void> {
    if (this.roomLeaveStarted) return;
    this.roomLeaveStarted = true;

    const userId = useGameStore.getState().localPlayerId;
    await beforeDisconnect;

    // 1. 소켓 연결 해제 (Presence untrack + player:leave 브로드캐스트)
    await this.socketService?.disconnect();
    this.socketService = undefined;

    if (this.offlineMode) {
      console.log('Offline mode - room leave skipped:', this.roomId);
      return;
    }

    // 2. DB에서 플레이어 제거 (room_players, tiles)
    try {
      await supabase.functions.invoke('room-leave', {
        body: {
          user_id: userId,
          room_id: this.roomId,
        },
      });
      console.log('Left room successfully:', this.roomId);
    } catch (error) {
      console.error('Failed to leave room:', error);
    }
  }

  update(_time: number, delta: number): void {
    // 게임 오버 상태면 업데이트 중지
    if (this.isGameOver) return;

    this.remoteCleanupTimer += delta;
    if (this.remoteCleanupTimer >= 500) {
      this.cleanupStaleRemotePlayers();
      this.remoteCleanupTimer = 0;
    }

    // Sticky 호스트 인계 판단: 봇 피드가 끊겼을 때만 가장 작은 ID가 인계
    this.evaluateHostTakeover();

    // 아이템 스폰/만료 (호스트 권위)
    if (this.isHost && !this.offlineMode) {
      this.itemManager?.tickHost(delta);
    }

    // 미사일 갱신 + 충돌 판정
    this.missileManager?.update();
    this.checkMissileHits();

    // 영역 스냅샷 주기 전송 (혼자가 아닐 때만)
    if (!this.offlineMode && this.lastPresentIds.length > 1) {
      this.territorySnapshotTimer += delta;
      if (this.territorySnapshotTimer >= this.TERRITORY_SNAPSHOT_INTERVAL_MS) {
        this.territorySnapshotTimer = 0;
        this.broadcastTerritorySnapshots();
      }
    }

    // 로컬 플레이어 업데이트
    if (this.localPlayer) {
      this.localPlayer.update(delta);

      // 역방향 효과 중 깜빡 경고
      this.setReverseWarning(this.localPlayer.effect.getActiveType() === 'reverse');

      const newGrid = this.localPlayer.getGridPosition();
      this.checkCollisions(newGrid.x, newGrid.y);

      // 플레이어가 봇의 꼬리와 충돌하는지 확인
      this.checkTrailCollisions();

      this.broadcastTimer += delta;
      if (this.broadcastTimer >= GAME_CONSTANTS.network.positionBroadcastInterval) {
        // 색상을 hex 문자열로 변환
        const colorHex = '#' + this.localPlayer.getColor().toString(16).padStart(6, '0');
        const storeState = useGameStore.getState();
        const localTerritoryPercent = this.territoryManager
          ? this.territoryManager.getTerritoryPercent(this.localPlayer.id)
          : storeState.territoryPercent;

        // trail은 부드러운 월드 좌표를 위치 2회당 1회(10Hz)만 포함
        // (그리드 좌표 trail은 수신측에서 지그재그로 보였음)
        this.moveBroadcastTick += 1;
        const includeTrail = this.moveBroadcastTick % 2 === 0;

        // 활성 효과를 함께 실어 원격 화면·충돌에 반영
        const activeType = this.localPlayer.effect.getActiveType();
        const effectPayload = activeType
          ? { type: activeType, remainingMs: this.localPlayer.effect.remainingMs(Date.now()) }
          : null;

        this.socketService?.sendMove(
          this.localPlayer.x,
          this.localPlayer.y,
          this.localPlayer.getDirection(),
          includeTrail ? this.localPlayer.getTrailPoints() : undefined,
          this.nickname,
          colorHex,
          storeState.score,
          localTerritoryPercent,
          effectPayload
        );
        this.broadcastTimer = 0;
      }

      // 캐릭터 위치 업데이트 (영역 분할 시 사용)
      this.territoryManager?.updateCharacterPosition(
        this.localPlayer.id,
        this.localPlayer.x,
        this.localPlayer.y
      );

      // 미니맵/HUD 갱신 스로틀: 영역 폴리곤이 커지면 매 프레임 변환·면적 계산이
      // 프레임 드랍의 원인이 되므로 300ms 주기로 제한 (시각적 차이 없음)
      this.hudUpdateTimer += delta;
      if (this.hudUpdateTimer >= 300) {
        this.hudUpdateTimer = 0;

        // 미니맵 업데이트 (로컬 플레이어 영역만 표시)
        const uiScene = this.scene.get('UIScene') as any;
        if (uiScene && uiScene.updateCircularMinimap && this.territoryManager) {
          const allTerritories = this.territoryManager.getMinimapData();
          const localId = useGameStore.getState().localPlayerId;
          const localTerritoryOnly = allTerritories.filter(t => t.playerId === localId);
          uiScene.updateCircularMinimap(
            localTerritoryOnly,
            this.localPlayer.x,
            this.localPlayer.y,
            this.worldCenterX,
            this.worldCenterY,
            this.worldRadius
          );
        }

        // 영역 비율 업데이트 (스토어 set은 구독자 전체를 깨우므로 같이 스로틀)
        if (this.territoryManager) {
          const localId = useGameStore.getState().localPlayerId;
          if (localId) {
            const percent = this.territoryManager.getTerritoryPercent(localId);
            useGameStore.getState().setTerritoryPercent(percent);
          }
        }
      }

      // 리더보드 데이터 수집 및 업데이트 (내부에서 500ms 쓰로틀)
      if (this.territoryManager) {
        this.updateLeaderboard();
      }
    }

    // AI 봇 업데이트 (호스트만)
    if (this.isHost) {
      this.botBroadcastTimer += delta;
      const shouldBroadcast = this.botBroadcastTimer >= GAME_CONSTANTS.network.botPositionBroadcastInterval;
      // trail은 페이로드가 크므로(최대 수백 포인트) 위치보다 낮은 주기로만 포함
      let includeTrail = false;
      if (shouldBroadcast) {
        this.botBroadcastTick += 1;
        includeTrail = this.botBroadcastTick % GAME_CONSTANTS.network.botTrailBroadcastEvery === 0;
      }

      for (const bot of this.aiBots) {
        // 봇 캐릭터 위치 업데이트 (영역 분할 시 사용)
        this.territoryManager?.updateCharacterPosition(bot.id, bot.x, bot.y);

        // 사냥꾼 봇에게 감지 범위 내 가장 가까운 플레이어 꼬리 지점 공급
        if (bot.isAggressive()) {
          bot.setHuntTarget(
            this.findNearestHostileTrailPoint(bot.x, bot.y, HUNTER_DETECTION_RADIUS)
          );
        }

        bot.update(delta);
        // 봇끼리 꼬리 충돌 확인
        this.checkBotTrailCollisions(bot);

        // 봇 영역이 모두 사라졌는지 확인 (최소 영역 이하면 사망)
        const botArea = this.territoryManager?.getTerritoryArea(bot.id) || 0;
        if (botArea < 1000) {
          console.log(`Bot ${bot.id} territory consumed - respawning!`);
          this.handleBotDeath(bot);
        }

        // 봇 이동 브로드캐스트 (활성 효과 동봉)
        if (shouldBroadcast) {
          const bt = bot.effect.getActiveType();
          const botEffect = bt
            ? { type: bt, remainingMs: bot.effect.remainingMs(Date.now()) }
            : null;
          this.socketService?.sendBotMove(
            bot.id,
            bot.x,
            bot.y,
            bot.getDirection(),
            includeTrail ? bot.getTrailPoints() : undefined,
            bot.getNickname(),
            bot.getColor(),
            botEffect
          );
        }
      }

      if (shouldBroadcast) {
        this.botBroadcastTimer = 0;
      }
    }

    // 원격 봇 업데이트 (비호스트만 - 호스트로부터 받은 봇 위치 표시)
    if (!this.isHost) {
      this.remoteBots.forEach((bot) => {
        bot.update(delta);
      });
    }

    // 원격 플레이어 업데이트
    this.remotePlayers.forEach((enemy) => {
      enemy.update(delta);
    });
  }

  private cleanupStaleRemotePlayers(): void {
    const now = Date.now();

    this.remotePlayers.forEach((enemy, id) => {
      if (enemy.isStale(now, GAME_CONSTANTS.network.remoteStaleTimeout)) {
        this.removeRemotePlayer(id, 'stale', false);
      }
    });

    // bot:move 피드가 오래 끊긴 원격 봇 정리
    // (봇 구성 축소 시 bot:remove가 유실됐거나 호스트 교체로 봇 수가 줄어든 경우)
    if (!this.isHost) {
      this.remoteBots.forEach((bot, id) => {
        if (bot.isStale(now, 8000)) {
          console.log('Removing stale remote bot:', id);
          bot.clearTrail();
          bot.destroy();
          this.remoteBots.delete(id);
          this.territoryManager?.removePlayerTerritory(id);
        }
      });
    }
  }

  private checkCollisions(x: number, y: number): void {
    if (!this.localPlayer || this.isGameOver) return;
    if (this.localPlayer.isInvincible) return; // 무적이면 적 꼬리 충돌 무시
    const gameState = useGameStore.getState();
    const localId = gameState.localPlayerId;
    const now = Date.now();

    // 상대 꼬리 절단 판정: 내 캐릭터가 상대의 꼬리를 밟으면 "꼬리 주인"이 죽는다
    // (봇과 동일한 paper.io 규칙 - 과거에는 반대로 밟은 쪽이 죽는 데다
    //  그리드 칸 정확 일치 판정이라 실제로는 거의 동작하지 않았다)
    for (const [enemyId, enemy] of this.remotePlayers) {
      if (enemy.isStale(now, GAME_CONSTANTS.network.remoteStaleTimeout)) {
        continue;
      }
      if (enemy.isInvincibleNow()) continue; // 무적 상대는 꼬리를 끊어도 안 죽음

      if (enemy.trail.length < 2) continue;

      // 마지막 trail 포인트와 상대의 실시간 위치 사이 구간(브로드캐스트 지연으로
      // 아직 trail에 없는 최신 ~100ms 구간)까지 이어서 판정한다.
      // 반경은 꼬리 띠 절반(~14px) + 내 큐브 절반(~14px) 근사이며,
      // 거대화한 상대는 꼬리가 굵으므로 크기 배율만큼 판정 반경을 키운다.
      const hitRadius = 24 * enemy.getSizeMultiplier();
      const trailWithHead = [...enemy.trail, { x: enemy.x, y: enemy.y }];
      if (!this.checkPointTrailCollision(this.localPlayer.x, this.localPlayer.y, trailWithHead, hitRadius, 0)) {
        continue;
      }

      console.log(`Cut enemy ${enemyId}'s trail - they die!`);
      this.audioManager?.play('kill');
      this.particleManager?.explode(enemy.x, enemy.y, enemy.getColor());

      const enemyTerritory = this.territoryManager?.getTerritory(enemyId);
      if (enemyTerritory && this.particleManager) {
        this.particleManager.territoryShatter(enemyTerritory.points, enemy.getColor());
      }

      // 사망 브로드캐스트 → 피해자 클라이언트가 받아 스스로 게임오버 처리
      void this.socketService?.killRemotePlayer(enemyId);
      // DB 기록 (킬 점수 RPC 포함) - 실패해도 게임 진행에는 영향 없음
      void this.invokePlayerDeathWithTimeout(enemyId, localId ?? this.playerId, 'TRAIL_CUT')
        .catch((error) => console.warn('Remote kill DB record failed:', error));

      this.removeRemotePlayer(enemyId, 'killed');

      useGameStore.getState().addScore(100);
      useGameStore.getState().addKill();
      break; // 프레임당 한 명만 처리
    }

    // 자신의 꼬리와 충돌
    const safeIndex = this.localPlayer.trail.length - 3;
    const hitSelf = this.localPlayer.trail.some(
      (t, index) => index < safeIndex && t.x === x && t.y === y
    );

    if (hitSelf) {
      console.log('Hit own tail!');
      this.handleSelfCollision();
    }
  }

  private broadcastLocalDeath(killerId?: string, type: string = 'PLAYER_DEATH'): Promise<void> {
    if (this.localDeathBroadcasted) {
      return this.localDeathBroadcastPromise ?? Promise.resolve();
    }

    const victimId = useGameStore.getState().localPlayerId;
    if (!victimId) return Promise.resolve();

    this.localDeathBroadcasted = true;
    this.localDeathBroadcastPromise = Promise.resolve(
      this.socketService?.sendPlayerDeath(victimId, killerId, type)
    ).then(() => undefined).catch((error) => {
      console.warn('Player death broadcast failed:', error);
    });

    return this.localDeathBroadcastPromise;
  }

  private invokePlayerDeathWithTimeout(
    victimId: string,
    killerId: string,
    type: string
  ): Promise<{ data: PlayerDeathFunctionData | null; error: unknown | null }> {
    const timeoutMs = 3000;
    const deathRequest = supabase.functions.invoke<PlayerDeathFunctionData>('player-death', {
      body: {
        victim_id: victimId,
        killer_id: killerId,
        collision_type: type,
        room_id: this.roomId,
      },
    });

    const timeout = new Promise<never>((_, reject) => {
      window.setTimeout(
        () => reject(new Error(`player-death timed out after ${timeoutMs}ms`)),
        timeoutMs
      );
    });

    return Promise.race([deathRequest, timeout]) as Promise<{
      data: PlayerDeathFunctionData | null;
      error: unknown | null;
    }>;
  }

  /**
   * 플레이어가 봇의 꼬리를 밟았는지 확인
   * 봇이 플레이어의 꼬리를 밟았는지 확인
   */
  private checkTrailCollisions(): void {
    if (!this.localPlayer) return;

    const playerX = this.localPlayer.x;
    const playerY = this.localPlayer.y;
    const collisionRadius = 24; // 꼬리 띠 절반 + 큐브 절반 근사 (겹쳐 보이면 킬)

    // 플레이어가 봇의 활성 꼬리를 밟으면 -> 봇 사망 (호스트: 로컬 봇 직접 처리)
    for (const bot of this.aiBots) {
      if (!bot.isTrailActive()) continue;
      if (bot.isInvincibleNow()) continue; // 무적 봇은 못 죽임

      const botTrail = bot.getTrailPoints();
      if (this.checkPointTrailCollision(playerX, playerY, botTrail, collisionRadius)) {
        console.log(`Player killed bot ${bot.id} by hitting its trail!`);
        this.audioManager?.play('kill');
        this.particleManager?.explode(bot.x, bot.y, bot.getColor());
        this.handleBotDeath(bot);

        // 점수 및 킬 추가
        useGameStore.getState().addScore(100);
        useGameStore.getState().addKill();
      }
    }

    // 비호스트: 원격 봇의 꼬리를 밟으면 호스트에게 봇 사망을 요청
    // (이전에는 봇 충돌 검사가 호스트의 로컬 봇만 대상이라,
    //  비호스트 화면의 봇 꼬리는 밟아도 아무 일도 일어나지 않았다)
    if (!this.isHost) {
      const nowMs = Date.now();
      this.remoteBots.forEach((remoteBot, botId) => {
        if (remoteBot.trail.length < 2) return;
        if (remoteBot.isInvincibleNow()) return; // 무적 봇은 못 죽임
        if ((this.botKillCooldown.get(botId) ?? 0) > nowMs) return;

        const trailWithHead = [...remoteBot.trail, { x: remoteBot.x, y: remoteBot.y }];
        if (!this.checkPointTrailCollision(playerX, playerY, trailWithHead, collisionRadius, 0)) {
          return;
        }

        this.botKillCooldown.set(botId, nowMs + 1500);
        console.log(`Player cut remote bot ${botId}'s trail - requesting kill`);
        this.audioManager?.play('kill');
        this.particleManager?.explode(remoteBot.x, remoteBot.y, remoteBot.getColor());
        this.socketService?.sendBotDeath(botId);

        // 로컬 예측: 호스트 응답(왕복 ~0.5초)을 기다리지 않고 즉시 죽은 것처럼 처리.
        // 혼자(=호스트) 플레이할 때와 동일한 즉각 반응을 만든다.
        // 호스트의 리스폰 bot:move(새 색/이름)가 도착하면 새 봇으로 다시 생성된다.
        const botTerritory = this.territoryManager?.getTerritory(botId);
        if (botTerritory && this.particleManager) {
          this.particleManager.territoryShatter(botTerritory.points, remoteBot.getColor());
        }
        this.territoryManager?.removePlayerTerritory(botId);

        // 죽기 직전 발사된 옛 정체성 패킷이 좀비처럼 깜빡이지 않게 잠시 억제
        this.botKillSuppress.set(botId, {
          until: nowMs + 2000,
          color: remoteBot.getColor(),
          nickname: remoteBot.getNickname(),
        });

        remoteBot.clearTrail();
        remoteBot.destroy();
        this.remoteBots.delete(botId);

        useGameStore.getState().addScore(100);
        useGameStore.getState().addKill();
      });
    }

    // 봇이 플레이어의 활성 꼬리를 밟으면 -> 플레이어 사망
    if (this.localPlayer.isTrailActive()) {
      const playerTrail = this.localPlayer.getTrailPoints();

      for (const bot of this.aiBots) {
        if (this.checkPointTrailCollision(bot.x, bot.y, playerTrail, collisionRadius)) {
          console.log(`Bot ${bot.id} killed player by hitting player's trail!`);
          useGameStore.getState().setDeathCause(`${bot.getNickname()}에게 꼬리를 잡혔어요!`);
          this.handleSelfCollision();
          return;
        }
      }
    }
  }

  /**
   * 봇끼리 꼬리 충돌 확인
   */
  private checkBotTrailCollisions(currentBot: AIBot): void {
    const collisionRadius = 15;

    for (const otherBot of this.aiBots) {
      if (currentBot.id === otherBot.id) continue;
      if (!otherBot.isTrailActive()) continue;

      const otherTrail = otherBot.getTrailPoints();
      if (this.checkPointTrailCollision(currentBot.x, currentBot.y, otherTrail, collisionRadius)) {
        console.log(`Bot ${currentBot.id} killed bot ${otherBot.id}!`);
        this.particleManager?.explode(otherBot.x, otherBot.y, otherBot.getColor());
        this.handleBotDeath(otherBot);
      }
    }

    // 현재 봇이 플레이어의 꼬리를 밟았는지 확인
    if (this.localPlayer?.isTrailActive()) {
      const playerTrail = this.localPlayer.getTrailPoints();
      if (this.checkPointTrailCollision(currentBot.x, currentBot.y, playerTrail, collisionRadius)) {
        console.log(`Bot ${currentBot.id} hit player's trail - player dies!`);
        useGameStore.getState().setDeathCause(`${currentBot.getNickname()}에게 꼬리를 잡혔어요!`);
        this.handleSelfCollision();
      }
    }

    // 봇이 "원격 플레이어"의 꼬리를 밟았는지 확인 (호스트가 봇을 대행해 판정)
    // 비호스트 클라이언트는 봇 판정을 하지 않으므로, 이 검사가 없으면
    // 봇이 비호스트 플레이어를 절대 죽이지 못한다.
    const nowMs = Date.now();
    this.remotePlayers.forEach((enemy, enemyId) => {
      if (enemy.isStale(nowMs, GAME_CONSTANTS.network.remoteStaleTimeout)) return;
      if (enemy.trail.length < 2) return;

      if (this.checkPointTrailCollision(currentBot.x, currentBot.y, enemy.trail, collisionRadius, 0)) {
        console.log(`Bot ${currentBot.id} cut remote player ${enemyId}'s trail!`);
        this.particleManager?.explode(enemy.x, enemy.y, enemy.getColor());
        void this.socketService?.killRemotePlayer(enemyId, currentBot.id);
        this.removeRemotePlayer(enemyId, 'killed by bot');
      }
    });
  }

  /**
   * 감지 범위 내에서 가장 가까운 "플레이어" 꼬리 지점 탐색 (사냥꾼 봇용, 호스트 전용)
   * 로컬 플레이어와 원격 플레이어의 꼬리를 모두 대상으로 한다.
   */
  private findNearestHostileTrailPoint(
    x: number,
    y: number,
    radius: number
  ): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestDistSq = radius * radius;

    const consider = (points: { x: number; y: number }[]) => {
      // 머리 근처 최신 포인트는 제외하고 몸통을 노린다
      for (let i = 0; i < points.length - 3; i++) {
        const dx = points[i].x - x;
        const dy = points[i].y - y;
        const distSq = dx * dx + dy * dy;
        if (distSq < bestDistSq) {
          bestDistSq = distSq;
          best = points[i];
        }
      }
    };

    if (this.localPlayer?.isTrailActive()) {
      consider(this.localPlayer.getTrailPoints());
    }

    const now = Date.now();
    this.remotePlayers.forEach((enemy) => {
      if (enemy.isStale(now, GAME_CONSTANTS.network.remoteStaleTimeout)) return;
      if (enemy.trail.length >= 2) {
        consider(enemy.trail);
      }
    });

    return best;
  }

  /**
   * 점이 트레일과 충돌하는지 확인
   * excludeTailPoints: 머리 쪽(최신)에서 제외할 포인트 수.
   * 자기/봇 자신의 꼬리 검사는 머리 근처가 항상 닿아 있으므로 제외가 필요하지만,
   * "남의 꼬리를 끊는" 판정은 끝부분까지 포함해야 추격전에서 킬이 된다.
   */
  private checkPointTrailCollision(
    px: number,
    py: number,
    trailPoints: { x: number; y: number }[],
    radius: number,
    excludeTailPoints: number = 3
  ): boolean {
    if (trailPoints.length < 2) return false;

    const checkUpTo = trailPoints.length - excludeTailPoints;

    for (let i = 0; i < checkUpTo - 1; i++) {
      const p1 = trailPoints[i];
      const p2 = trailPoints[i + 1];
      const dist = this.pointToSegmentDistance(px, py, p1.x, p1.y, p2.x, p2.y);

      if (dist < radius) {
        return true;
      }
    }

    return false;
  }

  /**
   * 점에서 선분까지의 거리
   */
  private pointToSegmentDistance(
    px: number, py: number,
    x1: number, y1: number,
    x2: number, y2: number
  ): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
    }

    let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t));

    const nearestX = x1 + t * dx;
    const nearestY = y1 + t * dy;

    return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
  }

  /**
   * 리더보드 업데이트
   */
  private leaderboardUpdateTimer: number = 0;
  private updateLeaderboard(): void {
    // 쓰로틀링: 500ms마다 업데이트
    this.leaderboardUpdateTimer += 16; // 약 60fps 기준
    if (this.leaderboardUpdateTimer < 500) return;
    this.leaderboardUpdateTimer = 0;

    if (!this.territoryManager || !this.localPlayer) return;

    const gameState = useGameStore.getState();
    const localId = gameState.localPlayerId;
    const entries: {
      id: string;
      nickname: string;
      score: number;
      territoryPercent: number;
      color: number;
      isLocal: boolean;
    }[] = [];

    // 로컬 플레이어 추가
    entries.push({
      id: localId || 'local',
      nickname: this.nickname,
      score: gameState.score,
      territoryPercent: this.territoryManager.getTerritoryPercent(localId || ''),
      color: this.localPlayer.getColor(),
      isLocal: true,
    });

    // AI 봇 추가 (호스트용)
    for (const bot of this.aiBots) {
      entries.push({
        id: bot.id,
        nickname: bot.getNickname(),
        score: 0,
        territoryPercent: this.territoryManager.getTerritoryPercent(bot.id),
        color: bot.getColor(),
        isLocal: false,
      });
    }

    // 원격 봇 추가 (비호스트용)
    this.remoteBots.forEach((remoteBot, botId) => {
      entries.push({
        id: botId,
        nickname: remoteBot.getNickname(),
        score: 0,
        territoryPercent: this.territoryManager!.getTerritoryPercent(botId),
        color: remoteBot.getColor(),
        isLocal: false,
      });
    });

    // 원격 플레이어 추가 (같은 방/Presence에 있는 실제 사용자 전체)
    const nowMs = Date.now();
    const leaderboardStaleMs = GAME_CONSTANTS.network.remoteStaleTimeout * 4;
    gameState.players.forEach((player, playerId) => {
      if (playerId === localId || !player.isAlive) return;
      // 이동 패킷을 한 번도 못 받은 항목(유령 DB 행 등)은 순위에서 제외
      if (!player.hasPosition) return;
      // 한참 동안 패킷이 없는 항목(끊김/게임오버 이벤트 유실)도 순위에서 제외
      if (player.lastSeenAt && nowMs - player.lastSeenAt > leaderboardStaleMs) return;

      const remotePlayer = this.remotePlayers.get(playerId);
      const localTerritoryEstimate = this.territoryManager?.getTerritoryPercent(playerId) ?? 0;
      const broadcastTerritoryPercent = player.territoryPercent ?? 0;
      const color = remotePlayer?.getColor()
        ?? Phaser.Display.Color.HexStringToColor(player.color || '#4D96FF').color;

      entries.push({
        id: playerId,
        nickname: player.nickname || remotePlayer?.getNickname() || 'Unknown',
        score: player.score || 0,
        territoryPercent: Math.max(localTerritoryEstimate, broadcastTerritoryPercent),
        color,
        isLocal: false,
      });
    });

    // 영역 비율로 정렬하여 1등 찾기
    const sorted = [...entries].sort((a, b) => b.territoryPercent - a.territoryPercent);
    const leaderId = sorted.length > 0 ? sorted[0].id : null;

    // 1등에게 왕관 표시 (호스트만 로컬 봇에 왕관)
    this.localPlayer.setCrown(leaderId === (localId || 'local'));
    if (this.isHost) {
      for (const bot of this.aiBots) {
        bot.setCrown(leaderId === bot.id);
      }
    }

    // UI에 전달
    const uiScene = this.scene.get('UIScene') as any;
    if (uiScene && uiScene.updateLeaderboard) {
      uiScene.updateLeaderboard(entries);
    }
  }
}
